import { useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import {
    ActionDependant,
    dataLocationToPath,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';

const CREATE_NEW_VALUE = '__create_new__';

/**
 * A SelectField that also allows inline creation of new entities.
 *
 * Uses the Syncable Object pattern: builds a { submission_url, data, status }
 * object and POSTs it via axios (same transport as DataSync) to create the
 * entity on the backend.
 *
 * YAML usage:
 *   - type: CreatableSelectField
 *     dataLocation: ~~.newProject.data.account
 *     dynamicOptions: ~.accountOptions
 *     optionsPath: ~~.accountOptions
 *     createSubmissionUrl: "/decoupled-api/save?display=decoupled&entity_type=node&bundle=account"
 *     createPlaceholder: "Nom du nouveau client"
 *     inputAttributes:
 *       class: "..."
 */
const CreatableSelectField = ({ props, path, datafield }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const inputAttributes = useEvaluatedAttributes(props.inputAttributes);

    const [isCreating, setIsCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    const options = evaluateTemplateValue({
        valueToEvaluate: props.dynamicOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: props.defaultFieldValue ?? '',
        globalDataContext,
        templateContext,
    });

    let optionsStorePath = null;
    if (props.optionsPath) {
        try {
            const raw = dataLocationToPath({
                dataLocation: props.optionsPath,
                currentPath: templateContext.templatePath,
                globalDataContext,
                templateContext,
            });
            optionsStorePath = raw?.startsWith('data.') ? raw.substring(5) : raw === 'data' ? '' : raw;
        } catch { /* ignore */ }
    }

    // Optional: reflect "the user is mid-creating a new option (text typed but
    // not yet submitted)" into a data path, so the embedding form can block its
    // own submit until the new option is created or cancelled. Without this the
    // typed value is silently lost when the form is saved before "Créer".
    let pendingStorePath = null;
    if (props.pendingPath) {
        try {
            const raw = dataLocationToPath({
                dataLocation: props.pendingPath,
                currentPath: templateContext.templatePath,
                globalDataContext,
                templateContext,
            });
            pendingStorePath = raw?.startsWith('data.') ? raw.substring(5) : raw === 'data' ? '' : raw;
        } catch { /* ignore */ }
    }
    const hasPendingNew = isCreating && newName.trim().length > 0;
    useEffect(() => {
        if (pendingStorePath === null || !globalDataContext?.updateData) return;
        globalDataContext.updateData(hasPendingNew, 'data.' + pendingStorePath);
    }, [hasPendingNew]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => () => {
        // Reset the flag if the field unmounts mid-creation (e.g. modal closed).
        if (pendingStorePath !== null && globalDataContext?.updateData) {
            globalDataContext.updateData(false, 'data.' + pendingStorePath);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const createSubmissionUrl = evaluateTemplateValue({
        valueToEvaluate: props.createSubmissionUrl,
        globalDataContext,
        templateContext,
    }) || '';

    const createPlaceholder = props.createPlaceholder || 'Nouveau nom…';

    const handleChange = useCallback((e) => {
        const val = e.target.value;
        if (val === CREATE_NEW_VALUE) {
            setIsCreating(true);
            setError(null);
            return;
        }
        setIsCreating(false);
        globalDataContext.updateData(val, formDataPath);
    }, [globalDataContext, formDataPath]);

    useEffect(() => {
        if (isCreating && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isCreating]);

    const handleCreate = useCallback(async () => {
        const trimmed = newName.trim();
        if (!trimmed || !createSubmissionUrl) return;

        const fieldName = props.createFieldName || 'title';
        const syncableObject = {
            submission_url: createSubmissionUrl,
            data: { [fieldName]: trimmed },
            status: null,
        };

        setIsSaving(true);
        setError(null);

        try {
            const response = await axios.post(syncableObject.submission_url, syncableObject);
            const result = response.data;

            const newId = String(result?.data?.id ?? result?.id ?? '');
            if (!newId) {
                setError('Création réussie mais ID manquant dans la réponse.');
                return;
            }

            const newOption = { value: newId, label: trimmed };

            if (optionsStorePath !== null) {
                const currentOptions = (optionsStorePath.split('.').reduce(
                    (obj, key) => obj?.[key],
                    globalDataContext.data,
                )) || [];
                globalDataContext.updateData(
                    [...currentOptions, newOption],
                    'data.' + optionsStorePath,
                );
            }

            globalDataContext.updateData(newId, formDataPath);
            setIsCreating(false);
            setNewName('');
        } catch (err) {
            const msg = err.response?.data?.status?.message || err.message || 'Erreur inconnue';
            setError(msg);
        } finally {
            setIsSaving(false);
        }
    }, [newName, createSubmissionUrl, optionsStorePath, globalDataContext, formDataPath]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCreate();
        }
        if (e.key === 'Escape') {
            setIsCreating(false);
            setNewName('');
            setError(null);
        }
    }, [handleCreate]);

    const handleCancel = useCallback(() => {
        setIsCreating(false);
        setNewName('');
        setError(null);
    }, []);

    const currentValue = formData ?? '';
    const { class: className, ...restAttrs } = inputAttributes || {};

    return (
        <ActionDependant {...props}>
            {!isCreating ? (
                <select
                    value={currentValue}
                    onChange={handleChange}
                    className={className}
                    {...restAttrs}
                >
                    <option value="">— Sélectionner —</option>
                    {Array.isArray(options) && options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    <option value={CREATE_NEW_VALUE}>＋ Créer un nouveau…</option>
                </select>
            ) : (
                <div className="flex gap-2 items-center">
                    <input
                        ref={inputRef}
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={createPlaceholder}
                        disabled={isSaving}
                        className={className}
                        style={{ flex: 1 }}
                    />
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={isSaving || !newName.trim()}
                        className="btn btn-primary"
                    >
                        {isSaving ? '…' : 'Créer'}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
                    >
                        ✕
                    </button>
                </div>
            )}
            {error && (
                <p className="text-red-600 text-xs mt-1">{error}</p>
            )}
        </ActionDependant>
    );
};

export default CreatableSelectField;
